import { Users, UserCheck, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import Reveal from "@/components/site/Reveal";

const steps = [
  { n: "01", title: "Accueil du groupe", desc: "Présentation, cadre et intention du travail." },
  { n: "02", title: "Mise en travail d'une problématique", desc: "Exploration accompagnée dans le groupe." },
  { n: "03", title: "Intégration de l'expérience", desc: "Temps de relecture et d'ancrage." },
];

export default function GroupConstellations({ onBookingClick }) {
  return (
    <section
      id="constellations"
      data-testid="constellations-section"
      className="relative py-24 md:py-32 bg-[#3A2E6E] text-white overflow-hidden"
    >
      {/* Decorative background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[40rem] h-[40rem] rounded-full bg-[#C98FA3]/15 blur-[140px]" />
        <div className="absolute bottom-0 left-0 w-[36rem] h-[36rem] rounded-full bg-[#2847B8]/30 blur-[140px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-7">
            <Reveal>
              <span className="text-sm uppercase tracking-[0.2em] text-[#C98FA3] font-medium">
                — Travail en groupe
              </span>
              <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl font-light mt-4 leading-[1.05] text-white">
                Constellations familiales en groupe
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <p className="text-lg text-white/80 leading-relaxed">
                Le travail en groupe permet de mettre en lumière des dynamiques familiales ou
                relationnelles à travers un cadre collectif structuré. Une approche complémentaire,
                souvent puissante, pour voir autrement ce qui se joue dans votre histoire.
              </p>
            </Reveal>
          </div>
        </div>

        {/* Two cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-20">
          <Reveal>
            <div
              data-testid="constellation-card-participant"
              className="h-full p-10 rounded-3xl bg-white/8 backdrop-blur-md border border-white/15 hover:border-[#C98FA3]/60 transition-colors"
            >
              <div className="w-12 h-12 rounded-full bg-[#C98FA3]/20 flex items-center justify-center mb-6">
                <Users className="w-5 h-5 text-[#E6D6DF]" />
              </div>
              <h3 className="font-serif-display text-3xl text-white">
                Participer avec sa problématique
              </h3>
              <p className="mt-5 text-base text-white/75 leading-relaxed">
                Vous venez avec une situation, une question ou une difficulté à explorer
                au sein du groupe. Le travail permet de révéler des dynamiques souvent
                invisibles dans un cadre accompagné.
              </p>
              <div className="mt-8 pt-6 border-t border-white/10 flex items-center justify-between text-sm">
                <span className="text-white/60 uppercase tracking-[0.18em] text-xs">
                  Tarif participant
                </span>
                <span className="font-serif-display text-xl text-[#E6D6DF]">110 €</span>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div
              data-testid="constellation-card-representant"
              className="h-full p-10 rounded-3xl bg-white/8 backdrop-blur-md border border-white/15 hover:border-[#C98FA3]/60 transition-colors"
            >
              <div className="w-12 h-12 rounded-full bg-[#C98FA3]/20 flex items-center justify-center mb-6">
                <UserCheck className="w-5 h-5 text-[#E6D6DF]" />
              </div>
              <h3 className="font-serif-display text-3xl text-white">
                Participer comme représentant
              </h3>
              <p className="mt-5 text-base text-white/75 leading-relaxed">
                Vous prenez part au travail du groupe sans venir avec votre propre constellation.
                Cette place permet souvent de vivre des prises de conscience profondes et
                de contribuer au processus collectif.
              </p>
              <div className="mt-8 pt-6 border-t border-white/10 flex items-center justify-between text-sm">
                <span className="text-white/60 uppercase tracking-[0.18em] text-xs">
                  Tarif représentant
                </span>
                <span className="font-serif-display text-xl text-[#E6D6DF]">30 €</span>
              </div>
            </div>
          </Reveal>
        </div>

        {/* How it works */}
        <Reveal>
          <p className="text-sm uppercase tracking-[0.2em] text-[#C98FA3] font-medium mb-8">
            — Le déroulé
          </p>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08}>
              <div
                data-testid={`constellation-step-${i}`}
                className="p-8 rounded-3xl bg-white/5 border border-white/10 h-full"
              >
                <span className="font-serif-display text-4xl text-[#C98FA3]">{s.n}</span>
                <h4 className="text-lg font-medium text-white mt-4">{s.title}</h4>
                <p className="mt-2 text-sm text-white/70 leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <p className="mt-16 max-w-3xl text-base text-white/70 font-serif-display italic leading-relaxed">
            Les ateliers se déroulent dans un cadre respectueux, confidentiel et structuré.
          </p>
        </Reveal>

        <Reveal delay={0.25}>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link
              to="/constellations-en-groupe"
              data-testid="constellation-cta-dates"
              className="group bg-white text-[#3A2E6E] hover:bg-[#E6D6DF] transition-all duration-300 rounded-full px-8 py-4 font-medium inline-flex items-center justify-center gap-2"
            >
              Découvrir le travail en groupe
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <button
              onClick={onBookingClick}
              data-testid="constellation-cta-info"
              className="border-2 border-white/30 text-white hover:bg-white/10 transition-all duration-300 rounded-full px-8 py-4 font-medium"
            >
              Poser une question
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
